// =============================================================
// EnquiryOps mock dataset — Enrichment Centre vertical
// Deterministic (seeded) so demo data is stable across reloads.
// This is the shape we expect to hydrate from HighLevel later.
// =============================================================

function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rand = mulberry32(20250199);
const pick = (arr) => arr[Math.floor(rand() * arr.length)];
const randInt = (min, max) => Math.floor(rand() * (max - min + 1)) + min;

// ---------- Reference data ----------

export const BRANCHES = [
  { id: "bukit-timah", name: "Bukit Timah", address: "170 Upper Bukit Timah Rd, #03-12", phone: "+65 6466 1122" },
  { id: "tampines", name: "Tampines", address: "10 Tampines Central 1, #04-21", phone: "+65 6788 3344" },
  { id: "punggol", name: "Punggol", address: "83 Punggol Central, #02-08", phone: "+65 6312 5566" },
];

export const PROGRAMMES = [
  { id: "pri-maths", name: "Primary Maths Mastery", levels: "P1–P6", capacity: 8, colour: "bg-sky-100 text-sky-800", weeklyClasses: 18 },
  { id: "sec-science", name: "Secondary Science Excel", levels: "Sec 1–4", capacity: 10, colour: "bg-teal-100 text-teal-800", weeklyClasses: 12 },
  { id: "coding", name: "Creative Coding & Robotics", levels: "7–14 yrs", capacity: 8, colour: "bg-indigo-100 text-indigo-800", weeklyClasses: 10 },
  { id: "speech-drama", name: "Speech & Drama", levels: "4–12 yrs", capacity: 12, colour: "bg-amber-100 text-amber-800", weeklyClasses: 8 },
  { id: "chinese", name: "Chinese Enrichment", levels: "P1–P6", capacity: 10, colour: "bg-rose-100 text-rose-800", weeklyClasses: 14 },
];

export const STAFF = [
  { id: "sarah", name: "Sarah Lim", role: "Centre Manager", initials: "SL", branch: "bukit-timah" },
  { id: "daniel", name: "Daniel Tan", role: "Admissions Lead", initials: "DT", branch: "tampines" },
  { id: "priya", name: "Priya Nair", role: "Ops Coordinator", initials: "PN", branch: "punggol" },
  { id: "weiling", name: "Chong Wei Ling", role: "Trial Coordinator", initials: "WL", branch: "bukit-timah" },
];

export const STAGES = [
  { id: "new_enquiry", name: "New Enquiry", group: "capture" },
  { id: "contacted", name: "Contacted", group: "capture" },
  { id: "qualified", name: "Qualified", group: "qualify" },
  { id: "vacancy_check", name: "Vacancy / Fit Check", group: "qualify" },
  { id: "slot_offered", name: "Slot Offered", group: "trial" },
  { id: "trial_offered", name: "Trial Offered", group: "trial" },
  { id: "trial_booked", name: "Trial Booked", group: "trial" },
  { id: "trial_attended", name: "Trial Attended", group: "trial" },
  { id: "follow_up", name: "Follow-Up", group: "convert" },
  { id: "enrolled", name: "Enrolled", group: "convert" },
  { id: "waitlist_recovery", name: "Waitlist Recovery", group: "retain" },
  { id: "re_enrolment_nudge", name: "Re-Enrolment Nudge", group: "retain" },
  { id: "review_referral", name: "Review / Referral Request", group: "retain" },
  { id: "lost", name: "Lost", group: "closed" },
];

export const SOURCES = ["WhatsApp", "Website Form", "Facebook / IG Ads", "Google Search", "Referral", "Walk-in", "Holiday Camp Campaign"];

// ---------- Lead generation ----------

const PARENT_SURNAMES = ["Tan", "Lim", "Lee", "Ng", "Wong", "Chen", "Goh", "Ong", "Teo", "Koh", "Chua", "Sim", "Yeo", "Ho", "Kumar", "Nair", "Rahman", "Hassan", "Pereira", "D'Souza"];
const PARENT_TITLES = ["Mrs", "Mr", "Ms", "Mdm", "Dr"];
const CHILD_NAMES = ["Ethan", "Chloe", "Kayden", "Sophie", "Lucas", "Emma", "Jayden", "Olivia", "Ryan", "Zara", "Marcus", "Aisha", "Isaac", "Mei Xin", "Aarav", "Hannah", "Javier", "Xin Yi", "Danish", "Charlotte", "Kai", "Elly", "Yu Chen", "Priyanka", "Nathan", "Sofia"];
const LEVELS = { "pri-maths": ["P1", "P2", "P3", "P4", "P5", "P6"], "sec-science": ["Sec 1", "Sec 2", "Sec 3", "Sec 4"], coding: ["Beginner", "Intermediate", "Advanced"], "speech-drama": ["Junior", "Lower Pri", "Upper Pri"], chinese: ["P1", "P2", "P3", "P4", "P5", "P6"] };
const SCHOOLS = ["Pei Hwa Presbyterian", "Methodist Girls' School", "St. Hilda's Primary", "Nan Chiau Primary", "Tampines Primary", "Punggol Green Primary", "ACS (Junior)", "Rosyth School", "Mee Toh School", "Horizon Primary"];
const PREFERRED_TIMES = ["Weekday 4–6pm", "Weekday 7–9pm", "Sat morning", "Sat afternoon", "Sun morning"];
const NEXT_ACTIONS = {
  new_enquiry: "Send first WhatsApp response",
  contacted: "Qualify child level & timing",
  qualified: "Check class vacancy with teacher",
  vacancy_check: "Awaiting teacher slot approval",
  slot_offered: "Chase parent on offered slot",
  trial_offered: "Confirm trial date with parent",
  trial_booked: "Send trial reminder (T-24h)",
  trial_attended: "Send post-trial feedback + offer",
  follow_up: "Decision call — day 3 follow-up",
  enrolled: "Schedule week-1 check-in",
  waitlist_recovery: "Offer newly opened slot",
  re_enrolment_nudge: "Send Term 3 renewal offer",
  review_referral: "Send Google review + referral link",
  lost: "Add to 90-day nurture list",
};

// stage weights → ~128 leads
const STAGE_WEIGHTS = [
  ["new_enquiry", 14], ["contacted", 16], ["qualified", 12], ["vacancy_check", 8],
  ["slot_offered", 9], ["trial_offered", 7], ["trial_booked", 10], ["trial_attended", 8],
  ["follow_up", 9], ["enrolled", 14], ["waitlist_recovery", 6], ["re_enrolment_nudge", 5],
  ["review_referral", 5], ["lost", 5],
];

const stagePool = STAGE_WEIGHTS.flatMap(([id, w]) => Array(w).fill(id));

const now = new Date();
function daysAgoISO(days, hourOffset = 0) {
  const d = new Date(now);
  d.setDate(d.getDate() - days);
  d.setHours(9 + (hourOffset % 10), randInt(0, 59), 0, 0);
  return d.toISOString();
}
function daysAheadISO(days, hour = 16) {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  d.setHours(hour, [0, 15, 30, 45][randInt(0, 3)], 0, 0);
  return d.toISOString();
}

export const LEADS = stagePool.map((stageId, i) => {
  const programme = PROGRAMMES[i % PROGRAMMES.length];
  const branch = BRANCHES[i % BRANCHES.length];
  const staff = STAFF[i % STAFF.length];
  const surname = pick(PARENT_SURNAMES);
  const title = pick(PARENT_TITLES);
  const childName = pick(CHILD_NAMES);
  const createdDaysAgo = randInt(0, 29);
  const stageIdx = STAGES.findIndex((s) => s.id === stageId);
  const hasTrialDate = ["trial_booked", "trial_attended", "follow_up", "enrolled"].includes(stageId);
  const firstResponseMins = randInt(2, 260);
  const value = programme.id === "sec-science" ? 420 : programme.id === "coding" ? 380 : 320;

  return {
    contact_id: `C-${1000 + i}`,
    opportunity_id: `OPP-${5000 + i}`,
    parent_name: `${title} ${surname}`,
    child_name: `${childName} ${surname}`,
    child_age: randInt(4, 16),
    child_level: pick(LEVELS[programme.id]),
    school_name: pick(SCHOOLS),
    phone: `+65 9${randInt(100, 999)} ${randInt(1000, 9999)}`,
    email: `${surname.toLowerCase().replace(/[^a-z]/g, "")}.${childName.toLowerCase().replace(/[^a-z]/g, "")}@gmail.com`,
    programme_id: programme.id,
    programme_name: programme.name,
    programme_interest: programme.name,
    branch_id: branch.id,
    branch_name: branch.name,
    source: pick(SOURCES),
    assigned_user: staff.id,
    assigned_user_name: staff.name,
    stage: stageId,
    stage_name: STAGES[stageIdx].name,
    preferred_days_times: pick(PREFERRED_TIMES),
    class_vacancy_status: stageId === "waitlist_recovery" ? "Waitlist" : stageId === "vacancy_check" ? "Checking" : "Available",
    trial_approval_status: ["slot_offered", "trial_offered", "trial_booked", "trial_attended", "follow_up", "enrolled"].includes(stageId) ? "Approved" : stageId === "vacancy_check" ? "Pending teacher approval" : "—",
    trial_date: hasTrialDate ? (stageId === "trial_booked" ? daysAheadISO(randInt(1, 6)) : daysAgoISO(randInt(1, 8))) : null,
    trial_attended: ["trial_attended", "follow_up", "enrolled"].includes(stageId),
    enrolment_status: stageId === "enrolled" ? "Enrolled" : "—",
    waitlist_status: stageId === "waitlist_recovery" ? "On waitlist — slot opening soon" : "—",
    re_enrolment_status: stageId === "re_enrolment_nudge" ? "Term 3 renewal pending" : "—",
    sibling_interest: rand() > 0.78,
    review_request_status: stageId === "review_referral" ? (rand() > 0.5 ? "Review link sent" : "Referral link sent") : "—",
    monetary_value: value,
    first_response_mins: firstResponseMins,
    sla_breached: firstResponseMins > 60,
    tags: [programme.id, branch.id, stageId === "waitlist_recovery" ? "waitlist" : "active"].filter(Boolean),
    created_date: daysAgoISO(createdDaysAgo, i),
    last_activity_date: daysAgoISO(Math.max(0, createdDaysAgo - randInt(0, 3)), i + 1),
    next_action: NEXT_ACTIONS[stageId],
    notes: stageId === "vacancy_check" ? `Parent prefers ${pick(PREFERRED_TIMES)}. Teacher to confirm capacity.` : `Enquired via ${pick(SOURCES)} about ${programme.name}.`,
  };
});

// ---------- Derived collections ----------

export const APPOINTMENTS = LEADS.filter((l) => l.trial_date).map((l, i) => ({
  appointment_id: `APT-${7000 + i}`,
  contact_id: l.contact_id,
  parent_name: l.parent_name,
  child_name: l.child_name,
  date_time: l.trial_date,
  calendar_name: `${l.programme_name} — Trial Class`,
  programme_id: l.programme_id,
  assigned_staff: l.assigned_user_name,
  status: l.stage === "trial_booked" ? "confirmed" : l.trial_attended ? "showed" : "no_show",
  location_branch: l.branch_name,
}));

const CONVO_TEMPLATES = [
  { in: "Hi, is there space for {child} ({level}) in {programme} at {branch}? Preferably {time}.", out: "Hi {parent}! Yes — we have 2 seats left for {level} {time}. Would {child} like a trial class this week?" },
  { in: "Saw your holiday camp ad. What are the fees?", out: "Great to hear from you! Camp runs 9am–1pm, $180 for 3 days. Shall I reserve a spot for {child}?" },
  { in: "Can we reschedule Saturday's trial?", out: "No problem — we have Sunday 10am or next Wed 5pm at {branch}. Which works better?" },
];

export const CONVERSATIONS = LEADS.slice(0, 24).map((l, i) => {
  const t = CONVO_TEMPLATES[i % CONVO_TEMPLATES.length];
  const fill = (s) => s.replace("{child}", l.child_name.split(" ")[0]).replace("{level}", l.child_level).replace("{programme}", l.programme_name).replace("{branch}", l.branch_name).replace("{time}", l.preferred_days_times).replace("{parent}", l.parent_name);
  return {
    conversation_id: `CONV-${3000 + i}`,
    contact_id: l.contact_id,
    channel: i % 5 === 3 ? "SMS" : i % 7 === 5 ? "Email" : "WhatsApp",
    unread: i % 6 === 0,
    last_response_timestamp: l.last_activity_date,
    messages: [
      { direction: "inbound", body: fill(t.in), at: l.created_date },
      { direction: "outbound", body: fill(t.out), at: l.last_activity_date, sender: l.assigned_user_name },
    ],
  };
});

export const REVIEWS = {
  requests_sent: 42,
  reviews_received: 31,
  average_rating: 4.8,
  referral_links_sent: 18,
  referrals_converted: 6,
  recent: [
    { id: "RV-1", parent: "Mrs Goh", rating: 5, snippet: "Ethan's confidence in Maths has soared. The trial process was so smooth.", programme: "Primary Maths Mastery", days_ago: 2 },
    { id: "RV-2", parent: "Mr Nair", rating: 5, snippet: "Booking the trial over WhatsApp took 2 minutes. Very organised centre.", programme: "Creative Coding & Robotics", days_ago: 5 },
    { id: "RV-3", parent: "Mdm Chua", rating: 4, snippet: "Teachers follow up personally after every class. Re-enrolled for Term 3.", programme: "Chinese Enrichment", days_ago: 9 },
  ],
};

// 30-day enquiry/trial series
export const DAILY_SERIES = Array.from({ length: 30 }, (_, i) => {
  const d = new Date(now);
  d.setDate(d.getDate() - (29 - i));
  const label = d.toLocaleDateString("en-SG", { day: "numeric", month: "short" });
  const enquiries = randInt(2, 9);
  return { day: label, enquiries, trials: Math.max(1, Math.round(enquiries * (0.35 + rand() * 0.2))), enrolments: Math.max(0, Math.round(enquiries * (0.15 + rand() * 0.15))) };
});

export const CAMPAIGNS = [
  {
    id: "camp-june",
    name: "June Holiday Camp Launch",
    status: "Active",
    channel: ["WhatsApp", "Email", "IG Ads"],
    enrolled_contacts: 64,
    conversions: 19,
    description: "Multi-touch camp promo to past enquirers and current parents. Early-bird pricing expires in 6 days.",
    steps: ["Day 0 — WhatsApp broadcast with camp brochure", "Day 2 — Email: schedule + early-bird code", "Day 5 — WhatsApp reminder to non-openers", "Day 7 — Last-call SMS + payment link"],
  },
  {
    id: "camp-term3",
    name: "Term 3 Re-Enrolment Nudges",
    status: "Active",
    channel: ["WhatsApp", "SMS"],
    enrolled_contacts: 88,
    conversions: 61,
    description: "Automated renewal sequence for current students, 3 weeks before term end. Priority seats held for 7 days.",
    steps: ["T-21d — WhatsApp: progress recap + renewal link", "T-14d — Teacher voice-note template", "T-7d — Seat release warning", "T-2d — Final call + sibling add-on offer"],
  },
  {
    id: "camp-sibling",
    name: "Sibling Referral Playbook",
    status: "Scheduled",
    channel: ["WhatsApp"],
    enrolled_contacts: 37,
    conversions: 6,
    description: "Targets enrolled families flagged with sibling interest. Offers a sibling trial + $50 referral credit.",
    steps: ["Trigger — enrolment confirmed + sibling flag", "Day 3 — Sibling trial invite", "Day 10 — Referral credit reminder", "On referral — thank-you + credit applied"],
  },
  {
    id: "camp-waitlist",
    name: "Waitlist Recovery Sprint",
    status: "Active",
    channel: ["WhatsApp", "SMS"],
    enrolled_contacts: 21,
    conversions: 9,
    description: "Fires the moment a seat opens. Waitlisted parents get first right of refusal with a 24h hold.",
    steps: ["Trigger — seat opened in class", "0h — WhatsApp slot offer with 24h hold", "12h — SMS reminder", "24h — Offer next parent in queue"],
  },
  {
    id: "camp-posttrial",
    name: "Post-Trial Follow-Up Sequence",
    status: "Active",
    channel: ["WhatsApp", "Email"],
    enrolled_contacts: 52,
    conversions: 28,
    description: "Every trial attendee gets teacher feedback within 4 hours, then a structured 5-day decision sequence.",
    steps: ["+4h — Teacher feedback note + photos", "Day 1 — Enrolment offer + fee schedule", "Day 3 — Handle objections call task", "Day 5 — Final seat-hold reminder"],
  },
  {
    id: "camp-review",
    name: "Review & Referral Booster",
    status: "Paused",
    channel: ["WhatsApp"],
    enrolled_contacts: 45,
    conversions: 31,
    description: "Requests a Google review 3 weeks post-enrolment, then converts happy parents into referrers.",
    steps: ["Week 3 — Review request with direct link", "On 5★ — Referral invite + credit", "On <4★ — Route to centre manager privately"],
  },
];

// ---------- KPI helpers ----------

const countByStage = (id) => LEADS.filter((l) => l.stage === id).length;

export const KPIS = {
  new_enquiries: LEADS.filter((l) => new Date(l.created_date) > new Date(now.getTime() - 30 * 864e5)).length,
  trials_booked: countByStage("trial_booked") + countByStage("trial_attended") + countByStage("follow_up") + countByStage("enrolled"),
  show_rate: 87,
  enrolments: countByStage("enrolled"),
  re_enrolment_nudges: countByStage("re_enrolment_nudge"),
  referrals: REVIEWS.referrals_converted,
  avg_first_response_mins: Math.round(LEADS.reduce((a, l) => a + l.first_response_mins, 0) / LEADS.length),
  waitlist_recovered: 9,
  admin_hours_saved: 11,
};

export const FUNNEL = [
  { stage: "Enquiry", count: LEADS.length },
  { stage: "Qualified", count: LEADS.length - countByStage("new_enquiry") - countByStage("contacted") - countByStage("lost") },
  { stage: "Slot Offered", count: countByStage("slot_offered") + countByStage("trial_offered") + countByStage("trial_booked") + countByStage("trial_attended") + countByStage("follow_up") + countByStage("enrolled") },
  { stage: "Trial", count: countByStage("trial_booked") + countByStage("trial_attended") + countByStage("follow_up") + countByStage("enrolled") },
  { stage: "Enrolled", count: countByStage("enrolled") },
  { stage: "Renewed / Referred", count: countByStage("review_referral") + REVIEWS.referrals_converted },
];

export const FOLLOW_UP_QUEUE = [
  ...LEADS.filter((l) => l.stage === "follow_up").slice(0, 3).map((l) => ({ lead: l, reason: "Trial attended — no decision yet", priority: "high", due: "Today" })),
  ...LEADS.filter((l) => l.stage === "waitlist_recovery").slice(0, 2).map((l) => ({ lead: l, reason: "Seat opened — offer new slot", priority: "high", due: "Today" })),
  ...LEADS.filter((l) => l.stage === "re_enrolment_nudge").slice(0, 2).map((l) => ({ lead: l, reason: "Term 3 renewal closing", priority: "medium", due: "Tomorrow" })),
  ...LEADS.filter((l) => l.stage === "review_referral").slice(0, 2).map((l) => ({ lead: l, reason: "Review request due", priority: "low", due: "This week" })),
];

export const SOURCE_PERFORMANCE = SOURCES.map((s) => {
  const subset = LEADS.filter((l) => l.source === s);
  return {
    source: s,
    enquiries: subset.length,
    trials: subset.filter((l) => ["trial_booked", "trial_attended", "follow_up", "enrolled"].includes(l.stage)).length,
    enrolments: subset.filter((l) => l.stage === "enrolled").length,
  };
}).filter((s) => s.enquiries > 0);

export const BRANCH_PERFORMANCE = BRANCHES.map((b) => {
  const subset = LEADS.filter((l) => l.branch_id === b.id);
  return {
    branch: b.name,
    enquiries: subset.length,
    trials: subset.filter((l) => ["trial_booked", "trial_attended", "follow_up", "enrolled"].includes(l.stage)).length,
    enrolments: subset.filter((l) => l.stage === "enrolled").length,
    avg_response: Math.round(subset.reduce((a, l) => a + l.first_response_mins, 0) / Math.max(1, subset.length)),
  };
});

export const STAFF_PERFORMANCE = STAFF.map((s) => {
  const subset = LEADS.filter((l) => l.assigned_user === s.id);
  return {
    staff: s.name,
    role: s.role,
    leads: subset.length,
    trials: subset.filter((l) => ["trial_booked", "trial_attended", "follow_up", "enrolled"].includes(l.stage)).length,
    enrolments: subset.filter((l) => l.stage === "enrolled").length,
    avg_response: Math.round(subset.reduce((a, l) => a + l.first_response_mins, 0) / Math.max(1, subset.length)),
    sla_met: Math.round((subset.filter((l) => !l.sla_breached).length / Math.max(1, subset.length)) * 100),
  };
});

const SLOT_APPROVAL_WAIT_MINUTES = [2, 9, 17, 25, 33, 41, 48, 55];

export const SLOT_QUEUE = [
    ...LEADS.filter((l) => l.stage === "vacancy_check").map((l, i) => ({
    id: `SQ-${l.contact_id}`,
    lead: l,
    type: "approval",
    request: `Trial slot request — ${l.programme_name} (${l.child_level})`,
    suggested_slot: l.preferred_days_times,
    capacity: `${randInt(0, 2)} of ${PROGRAMMES.find((p) => p.id === l.programme_id)?.capacity ?? 8} seats left`,
      waiting_since: new Date(Date.now() - SLOT_APPROVAL_WAIT_MINUTES[i] * 60_000).toISOString(),
  })),
  ...LEADS.filter((l) => l.stage === "slot_offered").slice(0, 4).map((l) => ({
    id: `SO-${l.contact_id}`,
    lead: l,
    type: "offered",
    request: `Slot offered — awaiting parent confirmation`,
    suggested_slot: l.preferred_days_times,
    capacity: "Held for 24h",
    waiting_since: l.last_activity_date,
  })),
];
